import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import bettingService from "../../../api/services/bettingService";
import { playSong } from "../../common/actions/common.actions";
import { IoIosArrowBack } from "react-icons/io";

const SelectedMathesDetail = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const { selectedMatchesDetail } = useSelector((state) => state.sidebar);

  const [selectedMatches, setSelectedMatches] = useState([]);

  const handleSongClick = async () => {
    dispatch(playSong());
  };

  const handleBackClick = async () => {
    await handleSongClick();
    navigate("/selected-matches");
  };

  useEffect(() => {
    setIsLoading(true);
    bettingService
      .bettingHistoryDetails({
        gamblingId: Number(id),
        status: "Inplay",
      })
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          const sortedData = data.slice().sort((a, b) => {
            const dateA = new Date(
              a.eventDate.split(" ")[0].split("-").reverse().join("-") +
                " " +
                a.eventDate.split(" ")[1]
            );
            const dateB = new Date(
              b.eventDate.split(" ")[0].split("-").reverse().join("-") +
                " " +
                b.eventDate.split(" ")[1]
            );
            return dateA - dateB;
          });
          setSelectedMatches(sortedData);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.status === 401) {
          navigate("/login");
        } else {
          console.error("API request error:", err);
        }
      });
  }, [id, navigate, setIsLoading]);

  return (
    <div className="flex h-screen w-full min-w-0 flex-1 flex-col overflow-hidden bg-gradient_start font-myanmarSansPro text-wback">
      {/* Header */}
      <header className="flex-none bg-header_green shadow-lg shadow-black/20">
        <div className="mx-auto flex min-h-[60px] w-full max-w-2xl items-center px-4 py-2">
        <button
          type="button"
          onClick={handleBackClick}
          aria-label="Back to selected matches"
          className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/10 transition hover:bg-white/20 active:scale-95"
        >
          <IoIosArrowBack className="h-5 w-5 text-wback" />
        </button>
        <h1 className="flex-1 truncate pr-10 text-center text-base font-semibold leading-[1.8] text-wback">
          {t("selectedMatches")}
        </h1>
        </div>
      </header>

      {/* Bet ID */}
      <div className="mx-auto w-full max-w-2xl flex-none px-4 pb-1.5 pt-2.5 text-right font-sans text-xs uppercase tracking-wide text-white/45">
        Bet ID: <span className="text-white/75">{selectedMatchesDetail?.postingNo || "—"}</span>
      </div>

      {/* Matches List */}
      <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto overscroll-contain px-4 pb-3">
        {selectedMatches.map((match, index) => {
          const bettedTeam =
            language === "en" ? match?.bettedTeam : match?.bettedTeamMyan;
          const oppositeTeam =
            language === "en"
              ? match?.oppositeTeamName
              : match?.oppositeTeamNameMyan;

          const bodyOdd = match.bodyOdd;

          const displayGame = match.isHome ? (
            <>
              {/* Home Team */}
              <div className="flex w-full items-center justify-between gap-3">
                <div className="min-w-0 truncate">
                  {bettedTeam}{" "}
                  <span className="text-yellow-400">
                    {match.isHomeBodyOdd &&
                      !match.overs &&
                      !match.under &&
                      `(${bodyOdd})`}
                    {match.overs && ` Gp(${match.goalOdd})`}
                  </span>
                </div>
                <div className="flex-none font-sans">{match.homeResult === "NS" ? "-" : match.homeResult}</div>
              </div>

              {/* Away Team */}
              <div className="mt-1 flex w-full items-center justify-between gap-3">
                <div className="min-w-0 truncate">
                  {oppositeTeam}{" "}
                  <span className="text-yellow-400">
                    {!match.isHomeBodyOdd &&
                      !match.overs &&
                      !match.under &&
                      `(${bodyOdd})`}
                    {match.under && `Gp(${match.goalOdd})`}
                  </span>
                </div>
                <div className="flex-none font-sans">{match.awayResult === "NS" ? "-" : match.awayResult}</div>
              </div>
            </>
          ) : (
            <>
              {/* Home Team (Opponent) */}
              <div className="flex w-full items-center justify-between gap-3">
                <div className="min-w-0 truncate">
                  {oppositeTeam}{" "}
                  <span className="text-yellow-400">
                    {match.isHomeBodyOdd &&
                      !match.overs &&
                      !match.under &&
                      `(${bodyOdd})`}
                  </span>
                </div>
                <div className="flex-none font-sans">{match.homeResult === "NS" ? "-" : match.homeResult}</div>
              </div>

              {/* Away Team (Betted) */}
              <div className="mt-1 flex w-full items-center justify-between gap-3">
                <div className="min-w-0 truncate">
                  {bettedTeam}{" "}
                  <span className="text-yellow-400">
                    {!match.isHomeBodyOdd &&
                      !match.overs &&
                      !match.under &&
                      `(${bodyOdd})`}
                  </span>
                </div>
                <div className="flex-none font-sans">{match.awayResult === "NS" ? "-" : match.awayResult}</div>
              </div>
            </>
          );

          return (
            <div
              key={index}
              className="my-2 flex flex-col rounded-lg border border-white/10 bg-header_green/55 px-4 py-3 shadow-sm shadow-black/10"
            >
              <div className="w-full font-sans text-[11px] text-white/40">{match.eventDate}</div>
              <div className="mt-2 flex w-full flex-col items-center justify-between text-sm font-semibold leading-[1.8] text-wback">
                {displayGame}
              </div>
              <div className="mt-1.5 flex w-full items-center justify-end text-xs font-medium leading-[1.8] text-emerald-200">
                {match?.home === true && <p className="  ">{bettedTeam}</p>}
                {match?.away === true && <p className="  ">{bettedTeam}</p>}
                {match?.overs === true && <p className="  ">{t("goalOver")}</p>}
                {match?.under === true && <p className=" ">{t("goalUnder")}</p>}
              </div>
            </div>
          );
        })}
      </main>

      <footer className="flex-none border-t border-white/10 bg-header_green/95 px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.18)]">
        <div className="mx-auto w-full max-w-2xl font-semibold">
        <div className="w-full rounded-lg bg-white/10 px-3 py-2 text-center text-sm leading-[1.8] text-white/70">
          {t("betAmount")} :{" "}
          <span className="font-sans text-sm font-bold text-emerald-200">
            {parseInt(selectedMatchesDetail?.amount || 0).toLocaleString()}
          </span>
        </div>
        {selectedMatchesDetail?.gamblingTypeId === 2 && (
          <div className="mt-1.5 flex gap-2">
            <span className="flex-1 rounded-lg bg-white/5 px-3 py-2 text-center text-xs leading-[1.8] text-white/55">
              {t("teamCount")} :{" "}
              <span className="font-sans font-bold text-white/85">
                {selectedMatchesDetail?.teamCount ?? "-"}
              </span>
            </span>
          </div>
        )}
        </div>
      </footer>
    </div>
  );
};

export default SelectedMathesDetail;
